import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

function ReviewCard({ ti, idx }) {
  return (
    <motion.figure
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: idx * 0.1 }}
      className="relative bg-white border border-dd-line rounded-2xl p-7 md:p-8 shadow-[0_12px_40px_-18px_rgba(15,46,32,0.25)] hover:border-dd-gold hover:shadow-xl transition-all"
      data-testid={`testimonial-${idx}`}
    >
      <Quote className="w-8 h-8 text-dd-gold/40" fill="currentColor" strokeWidth={0} />

      <div className="flex gap-0.5 mt-3">
        {Array.from({ length: ti.rating }).map((_, i) => (
          <Star key={i} className="w-4 h-4 text-dd-gold" fill="currentColor" strokeWidth={0} />
        ))}
      </div>

      <blockquote className="mt-4 text-dd-ink text-[0.98rem] leading-relaxed italic font-serif">
        “{ti.text}”
      </blockquote>

      <figcaption className="mt-6 pt-4 border-t border-dd-line">
        <div className="font-serif text-lg text-dd-ink">{ti.name}</div>
        <div className="text-xs text-dd-ink-muted tracking-wide mt-1">
          {ti.location}{ti.duration ? ` · ${ti.duration} subscriber` : ""}
        </div>
      </figcaption>
    </motion.figure>
  );
}

export default function Testimonials() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);

  useEffect(() => { api.get("/testimonials").then((r) => setItems(r.data)).catch((err) => { console.warn("testimonials fetch failed:", err?.message); }); }, []);
  if (!items.length) return null;

  return (
    <section className="dd-section bg-white relative overflow-hidden" data-testid="testimonials-section">
      <div className="absolute top-0 right-0 w-96 h-96 opacity-10 pointer-events-none"
           style={{ background: "radial-gradient(circle, #C9A24C 0%, transparent 70%)" }} />

      <div className="dd-container relative">
        <div className="text-center mb-14 max-w-2xl mx-auto">
          <div className="dd-label text-dd-gold">{t.testimonials.eyebrow}</div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-dd-ink mt-3">{t.testimonials.title}</h2>
          <div className="dd-ornament my-5"><span className="text-lg">✦</span></div>
          <p className="text-dd-ink-muted">{t.testimonials.subtitle}</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {items.map((ti, idx) => (
            <ReviewCard key={ti.id} ti={ti} idx={idx} />
          ))}
        </div>
      </div>
    </section>
  );
}
